import "./ResightingAssignments.css";
import { useContext, useEffect, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AppContext from "../../Context/AppContext";

function ResightingQCDashboard() {
  const BASE_URL_LOC = process.env.REACT_APP_SERVER_LOCATION;
  const appStates = useContext(AppContext);
  const navigate = useNavigate();
  const [qcStats, setQcStats] = useState({
    recordsWorkedOn: 0,
    approvedBySupervisor: 0,
  });
  const [isAssigning, setIsAssigning] = useState(false);
  const [claimError, setClaimError] = useState("");
  const [assignments, setAssignments] = useState([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");

  const showToast = (message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const fetchAssignments = async () => {
    setClaimError("");

    try {
      const authToken = appStates.getToken();
      if (!authToken) {
        setAssignments([]);
        setClaimError("Session expired. Please log in again.");
        return;
      }

      const response = await fetch(
        `${BASE_URL_LOC}/api/resighting/qc/assigned`,
        {
          method: "GET",
          redirect: "follow",
          headers: {
            "authentication-token": authToken,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Headers": "authentication-token",
            "Access-Control-Expose-Headers": "authentication-token",
          },
          mode: "cors",
        }
      );

      const body = await response.json();

      if (!response.ok) {
        setAssignments([]);
        setClaimError(
          body?.error || "Unable to load your current assignments right now."
        );
        return;
      }

      setAssignments(Array.isArray(body?.data) ? body.data : []);
    } catch (error) {
      setAssignments([]);
      setClaimError("Unable to load your current assignments right now.");
    }
  };

  const fetchQcStats = async () => {
    try {
      const authToken = appStates.getToken();
      if (!authToken) {
        setQcStats({
          recordsWorkedOn: 0,
          approvedBySupervisor: 0,
        });
        return;
      }

      const response = await fetch(`${BASE_URL_LOC}/api/resighting/qc/stats`, {
        method: "GET",
        redirect: "follow",
        headers: {
          "authentication-token": authToken,
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers": "authentication-token",
          "Access-Control-Expose-Headers": "authentication-token",
        },
        mode: "cors",
      });

      const body = await response.json();

      if (!response.ok) {
        setQcStats({
          recordsWorkedOn: 0,
          approvedBySupervisor: 0,
        });
        return;
      }

      setQcStats({
        recordsWorkedOn: Number(body?.data?.records_worked_on ?? 0),
        approvedBySupervisor: Number(body?.data?.approved_by_supervisor ?? 0),
      });
    } catch (error) {
      setQcStats({
        recordsWorkedOn: 0,
        approvedBySupervisor: 0,
      });
    }
  };

  useEffect(() => {
    fetchAssignments();
    fetchQcStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClaimResighting = async () => {
    setIsAssigning(true);
    setClaimError("");

    try {
      const authToken = appStates.getToken();
      if (!authToken) {
        showToast("Session expired. Please log in again.", "error");
        return;
      }

      const response = await fetch(`${BASE_URL_LOC}/api/resighting/qc/assign`, {
        method: "POST",
        redirect: "follow",
        headers: {
          "authentication-token": authToken,
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers": "authentication-token",
          "Access-Control-Expose-Headers": "authentication-token",
        },
        mode: "cors",
      });

      const body = await response.json();

      if (!response.ok) {
        const msg = body?.error || "Unable to assign a new record right now.";
        showToast(msg, "error");
        return;
      }

      if (body?.data == null) {
        showToast(
          "No records are available to assign right now. Try again later.",
          "warning"
        );
        return;
      }

      await fetchAssignments();
      await fetchQcStats();
      showToast("A new record has been assigned to you.", "success");
    } catch (error) {
      showToast("Unable to assign a new record right now.", "error");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleResume = (id) => {
    navigate(`/home/resighting/qc/record/${id}`);
  };

  return (
    <div className="resighting-qc-page">
      <div className="resighting-qc-stats-grid">
        <div className="resighting-qc-card">
          <p className="resighting-qc-card-label">Records worked on</p>
          <p className="resighting-qc-card-value">{qcStats.recordsWorkedOn}</p>
        </div>
        <div className="resighting-qc-card">
          <p className="resighting-qc-card-label">Approved by supervisor</p>
          <p className="resighting-qc-card-value">{qcStats.approvedBySupervisor}</p>
        </div>
      </div>

      <div className="resighting-qc-card">
        <h3 className="resighting-admin-heading">In progress</h3>
        <p className="resighting-admin-paragraph" style={{ marginTop: "0.4rem" }}>
          {assignments.length > 0
            ? `${assignments.length} record${
                assignments.length > 1 ? "s" : ""
              } you've claimed but not yet submitted.`
            : "-"}
        </p>

        {assignments.length > 0 && (
          <div className="resighting-qc-inprogress-list">
            {assignments.map((a) => (
              <div
                key={a.id}
                className="resighting-qc-inprogress-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "0.5rem 0",
                }}
              >
                <span className="resighting-qc-record-id">Record #{a.id}</span>
                <button
                  type="button"
                  className="resighting-qc-inprogress-resume-btn"
                  onClick={() => handleResume(a.id)}
                >
                  Resume
                </button>
              </div>
            ))}
          </div>
        )}

        {claimError ? <p className="resighting-qc-muted">{claimError}</p> : null}
      </div>

      <div className="resighting-qc-card">
        <div>
          <p className="resighting-admin-paragraph">Or start a fresh record.</p>
          <button
            type="button"
            className="resighting-qc-btn"
            onClick={handleClaimResighting}
            disabled={isAssigning}
          >
            {isAssigning ? "Assigning..." : "Assign me a new record"}
          </button>
        </div>
      </div>

      <Snackbar
        open={toastOpen}
        autoHideDuration={10000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ResightingQCDashboard;
